<template>
  <div>
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#FF6907" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- ══ BACK ACTION BANNER ══ -->
    <div class="rm-detail-header">
      <v-btn text class="back-btn" @click="$router.push('/rescuers')">
        <v-icon left>mdi-arrow-left</v-icon>
        Back to Directory
      </v-btn>
      <div class="rm-detail-header__title">
        <v-icon color="#4ADE80" class="mr-2" size="22">mdi-account-card-details-outline</v-icon>
        Rescuer Application Profile
      </div>
    </div>

    <!-- ══ MAIN CONTENT GRID ══ -->
    <v-row class="ma-0 pa-6" style="background-color: #F8FAFC; min-height: 100vh;">
      <!-- Left Column: Profile Card -->
      <v-col cols="12" md="4" lg="3">
        <v-card class="profile-summary-card pa-6 mb-4 elevation-0">
          <div class="d-flex flex-column align-center mb-6">
            <v-avatar size="140" class="avatar-frame mb-4 elevation-2 clickable" @click="dialog = true">
              <img :src="mediaURL + rescueruserdata.photo" alt="Photo" />
            </v-avatar>
            <h2 class="profile-name text-center mb-1">{{ rescueruserdata.name || '-' }}</h2>
            <v-chip small color="rgba(20,83,45,0.08)" text-color="#14532D" class="font-weight-bold">
              <v-icon left size="14">mdi-map-marker</v-icon>
              {{ rescuerdata.district || '-' }}
            </v-chip>
          </div>
          
          <v-divider class="my-4" />
          
          <div class="quick-details">
            <div class="quick-item mb-4">
              <div class="quick-label">Username</div>
              <div class="quick-val">{{ rescueruserdata.username || '-' }}</div>
            </div>
            <div class="quick-item mb-4">
              <div class="quick-label">Phone</div>
              <div class="quick-val">{{ rescueruserdata.phone || '-' }}</div>
            </div>
            <div class="quick-item mb-4">
              <div class="quick-label">Email</div>
              <div class="quick-val text-break">{{ rescueruserdata.email || '-' }}</div>
            </div>
            <div class="quick-item mb-2" v-if="rescuerdata.applieddate">
              <div class="quick-label">Applied Date</div>
              <div class="quick-val">{{ formatDate(rescuerdata.applieddate) }}</div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Right Column: Details Section Cards -->
      <v-col cols="12" md="8" lg="9">
        <!-- Personal Information -->
        <v-card class="detail-section-card pa-6 mb-6 elevation-0">
          <h3 class="section-title mb-4">
            <v-icon color="#14532D" class="mr-2" size="20">mdi-account-outline</v-icon>
            Personal Information
          </h3>
          <v-row>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Date of Birth</span>
              <span class="detail-value">{{ formatDate(rescueruserdata.dob) }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Blood Group</span>
              <span class="detail-value">{{ rescuerdata.bloodGroup || '-' }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Education</span>
              <span class="detail-value">{{ rescuerdata.education || '-' }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Employment</span>
              <span class="detail-value">{{ rescuerdata.employment || '-' }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Aadhar Number</span>
              <span class="detail-value">{{ rescuerdata.adharnumber || '-' }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Organization</span>
              <span class="detail-value">{{ rescueruserdata.organization || '-' }}</span>
            </v-col>
          </v-row>
        </v-card>

        <!-- Addresses -->
        <v-card class="detail-section-card pa-6 mb-6 elevation-0">
          <h3 class="section-title mb-4">
            <v-icon color="#14532D" class="mr-2" size="20">mdi-map-marker-outline</v-icon>
            Addresses
          </h3>
          <v-row>
            <v-col cols="12" sm="6" class="py-2">
              <span class="detail-label">Present Address</span>
              <span class="detail-value address-value">{{ rescuerdata.presentAddress || '-' }}</span>
            </v-col>
            <v-col cols="12" sm="6" class="py-2">
              <span class="detail-label">Permanent Address</span>
              <span class="detail-value address-value">{{ rescuerdata.permanentAddress || '-' }}</span>
            </v-col>
          </v-row>
        </v-card>

        <!-- Rescue & Medical Profile -->
        <v-card class="detail-section-card pa-6 mb-6 elevation-0">
          <h3 class="section-title mb-4">
            <v-icon color="#14532D" class="mr-2" size="20">mdi-clipboard-text-outline</v-icon>
            Rescue & Medical Profile
          </h3>
          <v-row>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Rescue Started At</span>
              <span class="detail-value">{{ rescuerdata.rescueStartedAt || '-' }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Any Offence History</span>
              <span class="detail-value">
                <v-chip v-if="rescuerdata.offence" small color="rgba(220,38,38,0.08)" text-color="#DC2626" class="font-weight-bold">
                  Yes
                </v-chip>
                <v-chip v-else small color="rgba(22,163,74,0.08)" text-color="#16A34A" class="font-weight-bold">
                  No
                </v-chip>
              </span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2" v-if="rescuerdata.offence && rescuerdata.offenceDetails">
              <span class="detail-label">Offence Details</span>
              <span class="detail-value text-error">{{ rescuerdata.offenceDetails }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <span class="detail-label">Any Disease Status</span>
              <span class="detail-value">
                <v-chip v-if="rescuerdata.diseaseStatus" small color="rgba(217,119,6,0.08)" text-color="#D97706" class="font-weight-bold">
                  Yes
                </v-chip>
                <v-chip v-else small color="rgba(22,163,74,0.08)" text-color="#16A34A" class="font-weight-bold">
                  No
                </v-chip>
              </span>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="py-2" v-if="rescuerdata.diseaseStatus && rescuerdata.diseaseDetails">
              <span class="detail-label">Disease Details</span>
              <span class="detail-value">{{ rescuerdata.diseaseDetails }}</span>
            </v-col>
          </v-row>
        </v-card>

        <!-- Training Details -->
        <v-card class="detail-section-card pa-6 mb-6 elevation-0">
          <h3 class="section-title mb-4">
            <v-icon color="#14532D" class="mr-2" size="20">mdi-school-outline</v-icon>
            Training Details
          </h3>
          <v-row>
            <v-col cols="12" sm="6" md="3" class="py-2">
              <span class="detail-label">Training Attended</span>
              <span class="detail-value">
                <v-chip v-if="rescuerdata.isTrainingAttended" small color="rgba(22,163,74,0.08)" text-color="#16A34A" class="font-weight-bold">
                  Yes
                </v-chip>
                <v-chip v-else small color="rgba(220,38,38,0.08)" text-color="#DC2626" class="font-weight-bold">
                  No
                </v-chip>
              </span>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="py-2" v-if="rescuerdata.isTrainingAttended && rescuerdata.trainingDetails && rescuerdata.trainingDetails.attendedDate">
              <span class="detail-label">Training Date</span>
              <span class="detail-value">{{ formatDate(rescuerdata.trainingDetails.attendedDate) }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="py-2" v-if="rescuerdata.isTrainingAttended && rescuerdata.trainingDetails && rescuerdata.trainingDetails.venue">
              <span class="detail-label">Training Venue</span>
              <span class="detail-value">{{ rescuerdata.trainingDetails.venue }}</span>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="py-2" v-if="rescuerdata.isTrainingAttended && rescuerdata.trainingDetails && rescuerdata.trainingDetails.certificateNumber">
              <span class="detail-label">Certificate Number</span>
              <span class="detail-value">{{ rescuerdata.trainingDetails.certificateNumber }}</span>
            </v-col>
          </v-row>
        </v-card>

        <!-- Uploaded Documents -->
        <v-card class="detail-section-card pa-6 mb-6 elevation-0" v-if="rescuerdata.adhar">
          <h3 class="section-title mb-4">
            <v-icon color="#14532D" class="mr-2" size="20">mdi-file-document-outline</v-icon>
            Uploaded Documents
          </h3>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <span class="detail-label mb-2">Aadhar Image</span>
              <v-card class="document-preview-card clickable overflow-hidden elevation-0" @click="dialogadhar = true">
                <v-img
                  contain
                  height="140"
                  :src="mediaURL + rescuerdata.adhar"
                  class="bg-grey-lighten-4"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <div class="document-preview-overlay d-flex align-center justify-center">
                  <v-icon color="white" size="24">mdi-magnify-plus</v-icon>
                  <span class="white--text ml-2 font-weight-bold">Zoom Document</span>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>

        <!-- Application Action Panel -->
        <v-card class="action-panel-card pa-6 mb-6 elevation-0">
          <v-row align="center" class="ma-0">
            <v-col cols="12" sm="4" class="py-2 px-2">
              <v-btn
                color="#10B981"
                block
                depressed
                large
                class="action-panel-btn action-panel-btn--approve"
                @click="approveDialog = true"
              >
                <v-icon left>mdi-account-check</v-icon>
                Approve Rescuer
              </v-btn>
            </v-col>
            <v-col cols="12" sm="4" class="py-2 px-2">
              <v-btn
                color="#3B82F6"
                block
                depressed
                large
                class="action-panel-btn action-panel-btn--training"
                @click="trainingDialog = true"
              >
                <v-icon left>mdi-account-cog</v-icon>
                Move To Training
              </v-btn>
            </v-col>
            <v-col cols="12" sm="4" class="py-2 px-2">
              <v-btn
                color="#EF4444"
                block
                depressed
                large
                class="action-panel-btn action-panel-btn--reject"
                @click="rejectDialog = true"
              >
                <v-icon left>mdi-account-remove</v-icon>
                Reject Rescuer
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- ══ DIALOGS ══ -->
    <v-dialog v-model="dialog" max-width="500">
      <v-card class="rounded-xl overflow-hidden">
        <v-img :src="mediaURL + rescueruserdata.photo" contain fill-height max-height="600"></v-img>
        <v-card-actions class="justify-end bg-white py-2 px-4">
          <v-btn color="#14532D" text @click="dialog = false" style="text-transform: none; font-weight: 600;">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogadhar" max-width="700">
      <v-card class="rounded-xl overflow-hidden">
        <v-img :src="mediaURL + rescuerdata.adhar" contain fill-height max-height="600"></v-img>
        <v-card-actions class="justify-end bg-white py-2 px-4">
          <v-btn color="#14532D" text @click="dialogadhar = false" style="text-transform: none; font-weight: 600;">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Approve Dialog -->
    <v-dialog v-model="approveDialog" max-width="500" persistent>
      <v-card class="rounded-xl overflow-hidden pa-4">
        <v-card-title class="headline font-weight-bold grey--text text--darken-3 pb-2">
          <v-icon color="#10B981" class="mr-2">mdi-account-check</v-icon>
          Approve Application
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-4">
            <span class="filter-label">Licence Number *</span>
            <v-text-field
              outlined
              dense
              v-model="licencenum"
              placeholder="e.g. LIC-2026-001"
              color="#14532D"
              hide-details
            />
          </div>
          <div class="mb-4">
            <span class="filter-label">Category *</span>
            <v-select
              outlined
              dense
              v-model="category"
              :items="categoryitems"
              placeholder="Select Category"
              color="#14532D"
              hide-details
            />
          </div>
          <div class="mb-4">
            <span class="filter-label">Is District Coordinator? *</span>
            <v-select
              outlined
              dense
              v-model="districtCoordinator"
              :items="districtCoordinatoritems"
              placeholder="Select Yes/No"
              color="#14532D"
              hide-details
            />
          </div>
        </v-card-text>
        <v-card-actions class="pb-2 pr-4 pt-4">
          <v-spacer></v-spacer>
          <v-btn
            tile
            outlined
            color="#EF4444"
            @click="approveDialog = false"
            style="border-radius: 8px; text-transform: none; font-weight: 600;"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="#14532D"
            @click="handlesubmit()"
            style="border-radius: 8px; text-transform: none; font-weight: 600; color: white;"
          >
            Approve
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Dialog -->
    <v-dialog v-model="rejectDialog" max-width="500" persistent>
      <v-card class="rounded-xl overflow-hidden pa-4">
        <v-card-title class="headline font-weight-bold grey--text text--darken-3 pb-2">
          <v-icon color="#EF4444" class="mr-2">mdi-account-remove</v-icon>
          Reject Application
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-4">
            <span class="filter-label">Rejection Remarks *</span>
            <v-textarea
              outlined
              v-model="remarks"
              placeholder="Explain the reason for rejecting this application..."
              color="#14532D"
              rows="3"
              hide-details
            />
          </div>
        </v-card-text>
        <v-card-actions class="pb-2 pr-4 pt-4">
          <v-spacer></v-spacer>
          <v-btn
            tile
            outlined
            color="#475569"
            @click="rejectDialog = false"
            style="border-radius: 8px; text-transform: none; font-weight: 600;"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="#EF4444"
            @click="rejectUser()"
            style="border-radius: 8px; text-transform: none; font-weight: 600; color: white;"
          >
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Move to Training Dialog -->
    <v-dialog v-model="trainingDialog" max-width="450" persistent>
      <v-card class="rounded-xl overflow-hidden pa-4">
        <v-card-title class="headline font-weight-bold grey--text text--darken-3 pb-2">
          <v-icon color="#3B82F6" class="mr-2">mdi-account-cog</v-icon>
          Move to Training List
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to move this applicant to the training candidates list? This will update their application state.
        </v-card-text>
        <v-card-actions class="pb-2 pr-4 pt-4">
          <v-spacer></v-spacer>
          <v-btn
            tile
            outlined
            color="#475569"
            @click="trainingDialog = false"
            style="border-radius: 8px; text-transform: none; font-weight: 600;"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="#3B82F6"
            @click="addTraining()"
            style="border-radius: 8px; text-transform: none; font-weight: 600; color: white;"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
    <script>
import axios from "axios";
export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      rescuerdata: [],
      rescueruserdata: [],
      dialog: false,
      dialogadhar: false,
      approveDialog: false,
      rejectDialog: false,
      trainingDialog: false,
      remarks: "",
      licencenum: "",
      categoryitems: ["Official", "Public"],
      districtCoordinator: "",
      category: "",
      districtCoordinatoritems: ["Yes", "No"],
    };
  },
  beforeMount() {
    this.getData();
  },
  methods: {
    formatDate(date) {
    if (!date) return '-';
    const [year, month, day] = date.slice(0, 10).split('-');
    return `${day}-${month}-${year}`;
  },
    openEditDialog(item) {
      this.$router.push({
        path: "/edituser",
        query: {
          id: item._id,
        },
      });
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/rescuer/get",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.rescuerdata = response.data.data;
              this.rescueruserdata = response.data.data.user;
              this.appLoading = false;
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    handlesubmit() {
      if (!this.licencenum) {
        this.msg = "Please Provide Licence Number.";
        this.showSnackBar = true;
        return;
      }
      if (this.districtCoordinator === "Yes") {
        this.districtCoordinatoredit = true;
      }
      if (this.districtCoordinator === "No") {
        this.districtCoordinatoredit = false;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/rescuer/approverescuer/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
          rescuerCode: this.licencenum,
          districtCoordinator: this.districtCoordinatoredit,
          idType: this.category,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.appLoading = false;
              this.approveDialog = false;
              this.$router.push({ path: "/newrescuerslist" });
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    updatepassword() {
      if (!this.password) {
        this.msg = "Please enter password to update.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/user/editpassword",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
          password: this.password,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.msg = "Password Updated Successfully.";
              this.showSnackBar = true;
              this.appLoading = false;
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    addTraining() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/rescuer/movetotraining/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.msg = "Moved to Training List.";
              this.showSnackBar = true;
              this.appLoading = false;
              this.trainingDialog = false;
              this.$router.push({ path: "/newrescuerslist" });
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    rejectUser() {
      if (!this.remarks) {
        this.msg = "Please provide remarks.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/rescuer/rejectrescuer/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
          reason: this.remarks,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.msg = "User Rejected Successfully.";
              this.showSnackBar = true;
              this.appLoading = false;
              this.trainingDialog = false;
              this.$router.push({ path: "/newrescuerslist" });
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
  },
};
</script>
<style scoped>
/* ── Header Banner ── */
.rm-detail-header {
  background: linear-gradient(135deg, #0A2318 0%, #14532D 55%, #166534 100%);
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}
.back-btn {
  color: rgba(255, 255, 255, 0.85) !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.01em !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border-radius: 8px !important;
  padding: 0 12px !important;
  height: 36px !important;
}
.back-btn:hover {
  background: rgba(255, 255, 255, 0.16) !important;
}
.rm-detail-header__title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
}

/* ── Profile Summary Card ── */
.profile-summary-card {
  background: #FFFFFF !important;
  border-radius: 12px !important;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.02) !important;
}
.avatar-frame {
  border: 4px solid #F1F5F9;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  transition: all 0.25s ease;
}
.avatar-frame:hover {
  transform: scale(1.03);
  border-color: #4ADE80;
}
.profile-name {
  font-size: 18px;
  font-weight: 800;
  color: #0F172A;
  font-family: 'Poppins', sans-serif;
}
.quick-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #94A3B8;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
}
.quick-val {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  word-break: break-all;
}

/* ── Info Cards ── */
.detail-section-card {
  background: #FFFFFF !important;
  border-radius: 12px !important;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.02) !important;
}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #1E293B;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #F1F5F9;
  padding-bottom: 10px;
}
.detail-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #94A3B8;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}
.detail-value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}
.address-value {
  line-height: 1.5;
  color: #475569;
  font-weight: 500;
}
.text-error {
  color: #EF4444;
}

/* ── Document Preview ── */
.document-preview-card {
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
  position: relative;
}
.document-preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.65);
  opacity: 0;
  transition: opacity 0.22s ease;
}
.document-preview-card:hover .document-preview-overlay {
  opacity: 1;
}

/* ── Action Panel ── */
.action-panel-card {
  background: #FFFFFF !important;
  border-radius: 12px !important;
  border: 1px dashed rgba(15, 23, 42, 0.12) !important;
}
.action-panel-btn {
  font-family: 'Inter', sans-serif !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  height: 48px !important;
  color: white !important;
}
.action-panel-btn--approve {
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2) !important;
}
.action-panel-btn--approve:hover {
  background-color: #059669 !important;
}
.action-panel-btn--training {
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2) !important;
}
.action-panel-btn--training:hover {
  background-color: #2563EB !important;
}
.action-panel-btn--reject {
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2) !important;
}
.action-panel-btn--reject:hover {
  background-color: #DC2626 !important;
}

.clickable {
  cursor: pointer;
}

.filter-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748B;
  margin-bottom: 6px;
}
</style>